// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Alert,
  AlertDescription,
  Separator,
} from '@roger/ui-components';
import {
  Loader2,
  ArrowLeft,
  Calendar,
  User,
  PlayCircle,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  History,
} from 'lucide-react';
import {
  QAVisit,
  startQAAnalysis,
  getLatestQAStatus,
  getAllVisitQAData,
} from '../services/rogerDocApi';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { VisitDataResponse } from '@/types/visitData.types';
import { getVisitTypeDisplayName } from '../hooks/useVisitTypeOptions';

interface QAVisitDetailsProps {
  visit: QAVisit;
  visitHnps: Array<{ name?: string; key: string }>;
  visitData: VisitDataResponse;
  loading: boolean;
  onBack: () => void;
  onOpenPdf: (pdfKey: string) => void;
  formatDate: (dateString: string) => string;
  getLatestFormAnswerState: () => unknown;
}

interface QAData {
  qa_id: string;
  status: string;
  created_at: string;
  qa_corrections?: Array<unknown>;
  qa_corrections_by_rule?: Array<unknown>;
  use_structured_qa_rules?: boolean;
  medical_data?: unknown;
  timings?: Array<{ name: string; time: number }>;
  cost?: Array<{
    name: string;
    cost_usd: number;
    input_tokens?: number;
    output_tokens?: number;
  }>;
  total_cost?: number;
}

const QA_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  INITIALIZED: 'INITIALIZED',
  PATIENT_INFO_SET: 'PATIENT_INFO_SET',
  QA_ANALYSIS_GENERATED: 'QA_ANALYSIS_GENERATED',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case QA_STATUS.COMPLETED:
      return 'bg-green-100 text-green-800 border-green-200';
    case QA_STATUS.FAILED:
      return 'bg-red-100 text-red-800 border-red-200';
    case QA_STATUS.CANCELLED:
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case QA_STATUS.NOT_STARTED:
    case QA_STATUS.INITIALIZED:
    case QA_STATUS.PATIENT_INFO_SET:
    case QA_STATUS.QA_ANALYSIS_GENERATED:
      return 'bg-blue-100 text-blue-800 border-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const isQAStatusInProgress = (status: string | undefined): boolean => {
  return (
    status === QA_STATUS.NOT_STARTED ||
    status === QA_STATUS.INITIALIZED ||
    status === QA_STATUS.PATIENT_INFO_SET ||
    status === QA_STATUS.QA_ANALYSIS_GENERATED
  );
};

const hasValidQAData = (qaData: QAData | null): boolean => {
  return !!(qaData && qaData.qa_id && qaData.status && qaData.created_at);
};

const QALegacyVisitDetails: React.FC<QAVisitDetailsProps> = ({
  visit,
  visitHnps,
  visitData,
  loading,
  onBack,
  onOpenPdf,
  formatDate,
  getLatestFormAnswerState,
}) => {
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );
  const [loadingQA, setLoadingQA] = useState(false);
  const [startingQA, setStartingQA] = useState(false);
  const [latestQAStatus, setLatestQAStatus] = useState<QAData | null>(null);
  const [allQAData, setAllQAData] = useState<QAData[]>([]);
  const [selectedQaId, setSelectedQaId] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    transcript: false,
    hnpSummary: false,
    formAnswers: false,
    medicalData: false,
    analysisMetrics: false,
  });

  // Fetch QA data
  const fetchQAData = useCallback(async () => {
    if (!visit.visit_id) return;

    setLoadingQA(true);
    try {
      const [latestStatus, allData] = await Promise.all([
        getLatestQAStatus(visit.visit_id),
        getAllVisitQAData(visit.visit_id),
      ]);
      setLatestQAStatus((latestStatus as QAData) || null);
      setAllQAData((allData as QAData[]) || []);

      // Auto-select the most recent QA if not already selected
      if (!selectedQaId && allData && (allData as QAData[]).length > 0) {
        setSelectedQaId((allData as QAData[])[0].qa_id);
      }
    } catch (error) {
      console.error('Error fetching QA data:', error);
    } finally {
      setLoadingQA(false);
    }
  }, [visit.visit_id, selectedQaId]);

  // Initial fetch
  useEffect(() => {
    if (visit.visit_id && !loading) {
      fetchQAData();
    }
  }, [visit.visit_id, loading, fetchQAData]);

  // Polling for in-progress QA
  useEffect(() => {
    if (
      !hasValidQAData(latestQAStatus) ||
      !isQAStatusInProgress(latestQAStatus.status)
    ) {
      return;
    }

    const pollInterval = setInterval(() => {
      fetchQAData();
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [latestQAStatus, fetchQAData]);

  const handleStartQA = async () => {
    setStartingQA(true);
    try {
      await startQAAnalysis(visit.visit_id, true);
      showSuccess('QA analysis started successfully');
      // Wait 3 seconds before fetching to give the backend time to initialize
      setTimeout(() => {
        fetchQAData();
      }, 3000);
    } catch (error) {
      console.error('Failed to start QA analysis:', error);
      showError('Failed to start QA analysis. Please try again.');
    } finally {
      setStartingQA(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const safeVisitHnps = visitHnps as Array<{ name?: string; key: string }>;

  // Get corrections count
  const getCorrectionsCount = (qa: QAData) => {
    return (qa.qa_corrections_by_rule || []).length;
  };

  // Format time in seconds to human readable format
  const formatTime = (seconds: number): string => {
    const roundedSeconds = Math.round(seconds);
    if (roundedSeconds < 60) {
      return `${roundedSeconds}s`;
    }
    const minutes = Math.floor(roundedSeconds / 60);
    const remainingSeconds = roundedSeconds % 60;
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  };

  // Format date to a readable format
  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Format cost in USD
  const formatCost = (cost: number): string => {
    return `$${cost.toFixed(6)}`;
  };

  // Get total time from timings array
  const getTotalTime = (qa: QAData): number => {
    if (!qa.timings || qa.timings.length === 0) return 0;
    const totalTiming = qa.timings.find(timing => timing.name === 'total');
    return totalTiming ? totalTiming.time : 0;
  };

  // Get the currently selected QA data
  const selectedQAData = selectedQaId
    ? allQAData.find(qa => qa.qa_id === selectedQaId) || latestQAStatus
    : latestQAStatus;

  return (
    <>
      <Button
        variant='outline'
        onClick={onBack}
        className='mb-6 border-blue-300 text-blue-700 hover:bg-blue-50'
      >
        <ArrowLeft className='w-4 h-4' />
        Back to Visit List
      </Button>

      {/* Visit Header */}
      <Card className='mb-6'>
        <CardContent>
          <div className='flex items-center justify-between'>
            <div className='space-y-2'>
              <div className='flex items-center gap-4'>
                <h2 className='text-xl font-bold text-gray-900'>
                  {visit.patient_first_name} {visit.patient_last_name}
                </h2>
                {visit.patient_id && (
                  <span className='text-sm text-gray-500 font-medium'>
                    MRN: {visit.patient_id}
                  </span>
                )}
              </div>
              <div className='text-xs text-gray-400 font-medium'>
                VISIT ID: {visit.visit_id}
              </div>
            </div>

            <div className='flex items-center gap-6'>
              <div className='flex items-center gap-2 text-gray-700'>
                <Calendar className='w-4 h-4 text-gray-500' />
                <span className='text-sm font-medium'>
                  {formatDate(visit.visit_date)}
                </span>
              </div>
              <div className='flex items-center gap-2 text-gray-700'>
                <User className='w-4 h-4 text-gray-500' />
                <span className='text-sm font-medium'>
                  {visit.nurse_username}
                </span>
              </div>
              <div className='flex items-center gap-2'>
                <Badge variant='secondary' className='text-sm font-medium'>
                  {getVisitTypeDisplayName(visit.visit_type)}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <div className='flex justify-center items-center py-12'>
          <div className='flex items-center gap-3 text-gray-500'>
            <Loader2 className='h-6 w-6 animate-spin text-blue-600' />
            <span>Loading visit details...</span>
          </div>
        </div>
      )}

      {/* QA Analysis Section */}
      {!loading && visitData && (
        <>
          {/* QA Analysis History - Show when there are multiple analyses */}
          {allQAData && allQAData.length > 1 && (
            <Card className='mb-6'>
              <CardHeader>
                <div className='flex items-center gap-2'>
                  <History className='w-5 h-5' />
                  <CardTitle className='text-lg font-semibold'>
                    QA Analysis History ({allQAData.length})
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className='overflow-x-auto'>
                  <div className='flex gap-3 pb-2'>
                    {allQAData.map((qaDoc, index) => {
                      const isSelected = qaDoc.qa_id === selectedQaId;
                      const totalCorrections = (
                        qaDoc.qa_corrections_by_rule || []
                      ).length;
                      const totalTime = getTotalTime(qaDoc);

                      return (
                        <div
                          key={qaDoc.qa_id}
                          className={`min-w-[280px] p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 bg-white hover:border-blue-300'
                          }`}
                          onClick={() => setSelectedQaId(qaDoc.qa_id)}
                        >
                          <div className='space-y-3'>
                            <div className='flex items-center justify-between'>
                              <div className='flex items-center gap-2 flex-wrap'>
                                <Badge
                                  className={`${getStatusColor(qaDoc.status)} border`}
                                >
                                  {qaDoc.status}
                                </Badge>
                              </div>
                              {index === 0 && (
                                <Badge
                                  variant='outline'
                                  className='text-xs border-blue-500 text-blue-600'
                                >
                                  <div className='flex items-center gap-1'>
                                    <div className='w-1 h-1 rounded-full bg-blue-600' />
                                    LATEST
                                  </div>
                                </Badge>
                              )}
                            </div>

                            <div className='text-xs text-gray-600 font-medium'>
                              {formatDateTime(qaDoc.created_at)}
                            </div>

                            {qaDoc.status === QA_STATUS.COMPLETED && (
                              <div className='flex flex-wrap items-center gap-2'>
                                <Badge variant='outline' className='text-xs'>
                                  {totalCorrections} CORRECTIONS
                                </Badge>
                                {qaDoc.total_cost && (
                                  <Badge
                                    variant='outline'
                                    className='text-xs text-green-700 border-green-300'
                                  >
                                    {formatCost(qaDoc.total_cost)}
                                  </Badge>
                                )}
                                {totalTime > 0 && (
                                  <Badge
                                    variant='outline'
                                    className='text-xs text-purple-700 border-purple-300'
                                  >
                                    {formatTime(totalTime)}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className='mb-6'>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <CardTitle className='text-lg font-semibold'>
                  QA Analysis
                </CardTitle>
                {hasValidQAData(selectedQAData) && (
                  <div className='flex items-center gap-4'>
                    <div className='flex items-center gap-2'>
                      <Badge
                        className={`${getStatusColor(selectedQAData.status)} border`}
                      >
                        {selectedQAData.status}
                      </Badge>
                      <span className='text-sm text-gray-600'>
                        {formatDateTime(selectedQAData.created_at)}
                      </span>
                    </div>
                    {selectedQAData.status === QA_STATUS.COMPLETED && (
                      <Button
                        variant='outline'
                        onClick={handleStartQA}
                        disabled={startingQA}
                      >
                        <RefreshCw
                          className={`w-4 h-4 ${startingQA ? 'animate-spin' : ''}`}
                        />
                        Rerun Analysis
                      </Button>
                    )}
                    {selectedQAData.status === QA_STATUS.FAILED && (
                      <Button
                        size='sm'
                        variant='destructive'
                        onClick={handleStartQA}
                        disabled={startingQA}
                      >
                        <RefreshCw
                          className={`w-4 h-4 ${startingQA ? 'animate-spin' : ''}`}
                        />
                        Retry Analysis
                      </Button>
                    )}
                    {isQAStatusInProgress(selectedQAData.status) && (
                      <Button
                        size='sm'
                        variant='outline'
                        onClick={fetchQAData}
                        disabled={loadingQA}
                      >
                        <RefreshCw
                          className={`w-4 h-4 ${loadingQA ? 'animate-spin' : ''}`}
                        />
                        Refresh
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!hasValidQAData(selectedQAData) && !loadingQA && (
                <div className='text-center py-8'>
                  <AlertCircle className='w-12 h-12 mx-auto mb-4 text-gray-400' />
                  <p className='text-gray-600 mb-4 font-medium'>
                    No analysis has been performed yet
                  </p>
                  <Button
                    onClick={handleStartQA}
                    size='lg'
                    disabled={startingQA}
                  >
                    <PlayCircle
                      className={`w-4 h-4 ${startingQA ? 'animate-spin' : ''}`}
                    />
                    Start QA Analysis
                  </Button>
                </div>
              )}

              {hasValidQAData(selectedQAData) &&
                isQAStatusInProgress(selectedQAData.status) && (
                  <Alert className='bg-blue-50 border-blue-200'>
                    <Clock className='h-4 w-4 text-blue-600' />
                    <AlertDescription className='text-blue-800'>
                      QA analysis in progress... Auto-refreshing every 3
                      seconds.
                    </AlertDescription>
                  </Alert>
                )}

              {/* QA Metadata Section */}
              {hasValidQAData(selectedQAData) && (
                <div className='space-y-4 mb-6'>
                  {/* Analysis Type */}
                  {selectedQAData.use_structured_qa_rules !== undefined && (
                    <div>
                      <div className='text-sm font-medium text-gray-700'>
                        Analysis Type
                      </div>
                      <div className='mt-1'>
                        <Badge
                          variant='secondary'
                          className='bg-green-100 text-green-800 border-green-200'
                        >
                          {selectedQAData.use_structured_qa_rules
                            ? 'STRUCTURED RULES'
                            : 'AI ANALYSIS'}
                        </Badge>
                      </div>
                    </div>
                  )}

                  {/* Analysis Metrics (Collapsible) */}
                  {((selectedQAData.timings &&
                    selectedQAData.timings.length > 0) ||
                    (selectedQAData.cost &&
                      selectedQAData.cost.length > 0)) && (
                    <div>
                      <button
                        onClick={() => toggleSection('analysisMetrics')}
                        className='flex items-center gap-2 w-full text-left font-medium text-sm hover:text-blue-600'
                      >
                        {expandedSections.analysisMetrics ? (
                          <ChevronUp className='w-4 h-4' />
                        ) : (
                          <ChevronDown className='w-4 h-4' />
                        )}
                        Analysis Metrics
                      </button>
                      {expandedSections.analysisMetrics && (
                        <div className='mt-3 p-3 bg-gray-50 rounded border'>
                          <div className='grid grid-cols-2 gap-6'>
                            {/* Analysis Time */}
                            {selectedQAData.timings &&
                              selectedQAData.timings.length > 0 && (
                                <div>
                                  <div className='text-sm font-medium text-gray-700 mb-2'>
                                    Analysis Time
                                  </div>
                                  <div className='space-y-1'>
                                    {selectedQAData.timings.map(
                                      (timing, index) => (
                                        <div
                                          key={index}
                                          className='flex justify-between text-sm'
                                        >
                                          <span className='text-gray-600'>
                                            {timing.name}:
                                          </span>
                                          <span className='text-gray-900 font-medium'>
                                            {formatTime(timing.time)}
                                          </span>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}

                            {/* Analysis Cost */}
                            {selectedQAData.cost &&
                              selectedQAData.cost.length > 0 && (
                                <div>
                                  <div className='text-sm font-medium text-gray-700 mb-2'>
                                    Analysis Cost
                                  </div>
                                  <div className='space-y-1'>
                                    {selectedQAData.cost.map(
                                      (costItem, index) => (
                                        <div
                                          key={index}
                                          className='flex justify-between text-sm'
                                        >
                                          <span className='text-gray-600 capitalize'>
                                            {costItem.name}:
                                          </span>
                                          <div className='flex gap-4'>
                                            <span className='text-gray-900 font-medium'>
                                              {formatCost(costItem.cost_usd)}
                                            </span>
                                            {(costItem.input_tokens ||
                                              costItem.output_tokens) && (
                                              <span className='text-gray-500 text-xs'>
                                                (In:{' '}
                                                {(
                                                  costItem.input_tokens || 0
                                                ).toLocaleString()}
                                                ) (Out:{' '}
                                                {(
                                                  costItem.output_tokens || 0
                                                ).toLocaleString()}
                                                )
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {hasValidQAData(selectedQAData) &&
                selectedQAData.status === QA_STATUS.COMPLETED && (
                  <div className='space-y-4'>
                    {/* QA Corrections */}
                    {getCorrectionsCount(selectedQAData) > 0 && (
                      <div className='space-y-4'>
                        <h4 className='font-medium text-sm'>
                          QA Corrections ({getCorrectionsCount(selectedQAData)})
                        </h4>
                        <div className='space-y-4'>
                          {selectedQAData.qa_corrections_by_rule?.map(
                            (
                              correction: Record<string, unknown>,
                              index: number
                            ) => (
                              <div
                                key={index}
                                className='p-4 border rounded-lg shadow-sm bg-white'
                              >
                                {/* Header: Title with severity badge and question ID */}
                                <div className='flex items-start justify-between mb-4'>
                                  <div className='flex-1'>
                                    <div className='flex items-center gap-3 mb-2'>
                                      <h5 className='text-base font-semibold text-gray-900'>
                                        {(correction.rule_name as string) ||
                                          'Correction'}
                                      </h5>
                                      {correction.severity && (
                                        <Badge
                                          variant={
                                            correction.severity === 'HIGH'
                                              ? 'destructive'
                                              : correction.severity === 'MEDIUM'
                                                ? 'default'
                                                : 'secondary'
                                          }
                                          className='text-xs font-semibold uppercase'
                                        >
                                          {correction.severity as string}
                                        </Badge>
                                      )}
                                    </div>
                                    {correction.question_id && (
                                      <Badge
                                        variant='outline'
                                        className='text-xs text-cyan-600 border-cyan-600'
                                      >
                                        {correction.question_id as string}
                                      </Badge>
                                    )}
                                  </div>
                                </div>

                                <div className='border-t border-gray-200 my-4'></div>

                                {/* Three-column layout */}
                                <div className='grid grid-cols-3 gap-4'>
                                  {/* Original Entry Column */}
                                  <div className='space-y-2'>
                                    <div className='text-sm font-semibold text-gray-600'>
                                      Original Entry
                                    </div>
                                    <div className='p-3 border rounded bg-gray-50 min-h-[60px]'>
                                      <div className='text-sm whitespace-pre-wrap'>
                                        {correction.original_entry !== null &&
                                        correction.original_entry !== undefined
                                          ? String(correction.original_entry)
                                          : '-'}
                                      </div>
                                    </div>
                                  </div>

                                  {/* AI Corrected Entry Column */}
                                  <div className='space-y-2'>
                                    <div className='text-sm font-semibold text-gray-600'>
                                      AI Corrected Entry
                                    </div>
                                    <div className='p-3 border rounded bg-teal-50 min-h-[60px]'>
                                      <div className='space-y-2'>
                                        <div className='text-sm whitespace-pre-wrap'>
                                          {correction.ai_corrected_entry !==
                                            null &&
                                          correction.ai_corrected_entry !==
                                            undefined
                                            ? String(
                                                correction.ai_corrected_entry
                                              )
                                            : '-'}
                                        </div>
                                        {/* Multiple EMR Corrections */}
                                        {correction.emr_corrections &&
                                          Array.isArray(
                                            correction.emr_corrections
                                          ) &&
                                          (
                                            correction.emr_corrections as Array<
                                              Record<string, unknown>
                                            >
                                          ).map(
                                            (
                                              emrCorrection: Record<
                                                string,
                                                unknown
                                              >,
                                              emrIndex: number
                                            ) => (
                                              <div
                                                key={emrIndex}
                                                className='p-2 bg-white/70 rounded mt-2'
                                              >
                                                <div className='text-xs font-semibold text-gray-600 mb-1'>
                                                  EMR Correction #{emrIndex + 1}
                                                  :
                                                </div>
                                                {emrCorrection.ai_corrected_answer_id && (
                                                  <div className='flex gap-2 items-start mb-1'>
                                                    <span className='text-xs font-semibold text-gray-600'>
                                                      Answer ID:
                                                    </span>
                                                    <span className='text-xs font-medium font-mono'>
                                                      {String(
                                                        emrCorrection.ai_corrected_answer_id
                                                      )}
                                                    </span>
                                                  </div>
                                                )}
                                                {emrCorrection.ai_corrected_answer_value !==
                                                  null &&
                                                  emrCorrection.ai_corrected_answer_value !==
                                                    undefined && (
                                                    <div className='flex gap-2 items-start'>
                                                      <span className='text-xs font-semibold text-gray-600'>
                                                        Answer Value:
                                                      </span>
                                                      <span className='text-xs font-medium'>
                                                        {String(
                                                          emrCorrection.ai_corrected_answer_value
                                                        )}
                                                      </span>
                                                    </div>
                                                  )}
                                              </div>
                                            )
                                          )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Justification & Source Column */}
                                  <div className='space-y-2'>
                                    <div className='text-sm font-semibold text-gray-600'>
                                      Justification & Source
                                    </div>
                                    <div>
                                      <div className='text-sm whitespace-pre-wrap mb-3'>
                                        {correction.justification
                                          ? String(correction.justification)
                                          : '-'}
                                      </div>
                                      {correction.sources &&
                                        Array.isArray(correction.sources) &&
                                        (correction.sources as string[])
                                          .length > 0 && (
                                          <div className='flex flex-wrap gap-2'>
                                            {(
                                              correction.sources as string[]
                                            ).map(
                                              (source: string, idx: number) => {
                                                const formattedSource =
                                                  source.startsWith('@')
                                                    ? source
                                                        .substring(1)
                                                        .toUpperCase()
                                                    : source.toUpperCase();
                                                return (
                                                  <Badge
                                                    key={idx}
                                                    variant='outline'
                                                    className='text-xs'
                                                  >
                                                    {formattedSource}
                                                  </Badge>
                                                );
                                              }
                                            )}
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                </div>

                                {/* Confidence Score */}
                                {correction.confidence_score && (
                                  <div className='flex justify-start mt-4'>
                                    <Badge
                                      variant='outline'
                                      className='text-xs'
                                    >
                                      CONFIDENCE:{' '}
                                      {Math.round(
                                        (correction.confidence_score as number) *
                                          100
                                      )}
                                      %
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {getCorrectionsCount(selectedQAData) === 0 && (
                      <div className='text-center py-8 text-gray-500'>
                        <CheckCircle className='w-12 h-12 mx-auto mb-2 text-green-500' />
                        <p className='font-medium'>No Issues Found</p>
                        <p className='text-sm'>
                          The visit form answers look good!
                        </p>
                      </div>
                    )}

                    {/* Medical Data */}
                    {selectedQAData.medical_data && (
                      <div>
                        <button
                          onClick={() => toggleSection('medicalData')}
                          className='flex items-center gap-2 w-full text-left font-medium text-sm hover:text-blue-600'
                        >
                          {expandedSections.medicalData ? (
                            <ChevronUp className='w-4 h-4' />
                          ) : (
                            <ChevronDown className='w-4 h-4' />
                          )}
                          Medical Data
                        </button>
                        {expandedSections.medicalData && (
                          <pre className='text-xs bg-gray-50 p-3 rounded border overflow-auto max-h-96 mt-2'>
                            {JSON.stringify(
                              selectedQAData.medical_data,
                              null,
                              2
                            )}
                          </pre>
                        )}
                      </div>
                    )}
                  </div>
                )}
            </CardContent>
          </Card>

          <Separator className='my-6' />

          {/* Visit Information Section */}
          <div className='space-y-4'>
            <h3 className='text-lg font-semibold'>Visit Information</h3>

            {/* Transcript Section */}
            {visitData.transcript && (
              <Card>
                <CardHeader>
                  <button
                    onClick={() => toggleSection('transcript')}
                    className='flex items-center justify-between w-full text-left'
                  >
                    <CardTitle className='text-base'>Transcript</CardTitle>
                    {expandedSections.transcript ? (
                      <ChevronUp className='w-4 h-4' />
                    ) : (
                      <ChevronDown className='w-4 h-4' />
                    )}
                  </button>
                </CardHeader>
                {expandedSections.transcript && (
                  <CardContent>
                    <div className='text-sm whitespace-pre-wrap max-h-96 overflow-y-auto'>
                      {String(visitData.transcript)}
                    </div>
                  </CardContent>
                )}
              </Card>
            )}

            {/* HnP Summary Section */}
            {visitData.hnp_summary && (
              <Card>
                <CardHeader>
                  <button
                    onClick={() => toggleSection('hnpSummary')}
                    className='flex items-center justify-between w-full text-left'
                  >
                    <CardTitle className='text-base'>HnP Summary</CardTitle>
                    {expandedSections.hnpSummary ? (
                      <ChevronUp className='w-4 h-4' />
                    ) : (
                      <ChevronDown className='w-4 h-4' />
                    )}
                  </button>
                </CardHeader>
                {expandedSections.hnpSummary && (
                  <CardContent>
                    <pre className='text-xs bg-gray-50 p-3 rounded border overflow-auto max-h-96'>
                      {typeof visitData.hnp_summary === 'string'
                        ? visitData.hnp_summary
                        : JSON.stringify(visitData.hnp_summary, null, 2)}
                    </pre>
                  </CardContent>
                )}
              </Card>
            )}

            {/* HnP PDFs Section */}
            {safeVisitHnps.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className='text-base'>
                    History & Physical Documents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-2'>
                    {safeVisitHnps.map((hnp, index) => (
                      <div
                        key={index}
                        className='flex items-center justify-between p-2 border rounded'
                      >
                        <span className='text-sm'>
                          {hnp &&
                          typeof hnp === 'object' &&
                          'name' in hnp &&
                          hnp.name
                            ? String(hnp.name)
                            : `Document ${index + 1}`}
                        </span>
                        <Button
                          size='sm'
                          variant='outline'
                          onClick={() => {
                            if (
                              hnp &&
                              typeof hnp === 'object' &&
                              'key' in hnp
                            ) {
                              onOpenPdf(String(hnp.key));
                            }
                          }}
                        >
                          View PDF
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Form Answer State */}
            {getLatestFormAnswerState() && (
              <Card>
                <CardHeader>
                  <button
                    onClick={() => toggleSection('formAnswers')}
                    className='flex items-center justify-between w-full text-left'
                  >
                    <CardTitle className='text-base'>
                      Latest Form Answers
                    </CardTitle>
                    {expandedSections.formAnswers ? (
                      <ChevronUp className='w-4 h-4' />
                    ) : (
                      <ChevronDown className='w-4 h-4' />
                    )}
                  </button>
                </CardHeader>
                {expandedSections.formAnswers && (
                  <CardContent>
                    <pre className='text-xs bg-gray-50 p-3 rounded border overflow-auto max-h-96'>
                      {JSON.stringify(getLatestFormAnswerState(), null, 2)}
                    </pre>
                  </CardContent>
                )}
              </Card>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default QALegacyVisitDetails;
